import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { ButtonDirective } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { ProgressBar } from 'primeng/progressbar';
import { DuplicateRequest, DuplicateRun, ProjectSummary } from '../../models';
import { deriveSlug } from '../../utils/slug';
import { formatBytes } from '../../utils/format';
import { ASPECT_OPTIONS, ASPECT_PRESETS, deriveDimensionsMap, matchAspectPreset } from '../../utils/aspect-presets';

/**
 * Duplicate-project dialog (DEV-1091). Two states in one dialog: a settings
 * form before confirmation, and progress once the copy is running — the copy
 * takes long enough (hundreds of media files) that handing the user an
 * unexplained wait would be worse than showing the bar.
 *
 * Everything is prefilled from the project being copied, so confirming
 * without touching anything is a valid action; the name and slug get a
 * "-copy" suffix because those two can't be left identical.
 *
 * The parent owns the HTTP calls and passes the live `run` back in — the
 * dialog stays a view over whatever the parent is polling, so closing it
 * doesn't cancel the copy and reopening shows the current state.
 */

const NAME_SUFFIX = ' copy';
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

@Component({
	selector: 'app-duplicate-project-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, ButtonDirective, InputText, InputNumber, Select, ProgressBar, FormsModule],
	templateUrl: './duplicate-project-dialog.html',
	styleUrl: './duplicate-project-dialog.scss',
})
export class DuplicateProjectDialogComponent {
	visible = input<boolean>(false);
	/** The project being copied. Drives every prefilled value. */
	project = input<ProjectSummary | null>(null);
	/** Source project's coordinate space, for prefilling the format. */
	sourceWidth = input<number>(1920);
	sourceHeight = input<number>(1080);
	/** Slugs already used in this campaign — the project part only, as the user types it. */
	takenSlugs = input<string[]>([]);
	/** Live run once the copy has started; null while the form is showing. */
	run = input<DuplicateRun | null>(null);
	/** Rejection from the start request (taken slug, access, …). */
	startError = input<string | null>(null);
	starting = input<boolean>(false);

	visibleChange = output<boolean>();
	confirmed = output<DuplicateRequest>();
	/** Emitted when the user chooses to open the finished copy. */
	openCopy = output<DuplicateRun>();

	protected readonly name = signal('');
	protected readonly slug = signal('');
	protected readonly aspect = signal('16:9');
	protected readonly width = signal(1920);
	protected readonly height = signal(1080);
	/** True once the user edits the slug by hand, after which the name stops driving it. */
	private readonly slugTouched = signal(false);

	protected readonly aspectOptions = ASPECT_OPTIONS;

	constructor() {
		// Reseed every time the dialog opens, so a second duplication doesn't
		// inherit the previous one's edits.
		effect(() => {
			if (!this.visible()) return;
			const project = this.project();
			if (!project) return;
			this.name.set(`${project.name}${NAME_SUFFIX}`);
			this.slug.set(`${deriveSlug(project.name)}-copy`);
			this.slugTouched.set(false);
			this.width.set(this.sourceWidth());
			this.height.set(this.sourceHeight());
			this.aspect.set(matchAspectPreset(this.sourceWidth(), this.sourceHeight()));
		});
	}

	// ── Form state ───────────────────────────────────────────────────────────

	protected onNameChange(value: string): void {
		this.name.set(value);
		// The slug follows the name until the user takes it over — same
		// behaviour as the New Project form.
		if (!this.slugTouched()) this.slug.set(deriveSlug(value));
	}

	protected onSlugChange(value: string): void {
		this.slugTouched.set(true);
		this.slug.set(value);
	}

	protected onAspectChange(value: string): void {
		this.aspect.set(value);
		const preset = ASPECT_PRESETS[value];
		if (preset) {
			this.width.set(preset.width);
			this.height.set(preset.height);
		}
	}

	protected onWidthChange(value: number | null): void {
		this.width.set(value ?? 0);
		this.aspect.set(matchAspectPreset(this.width(), this.height()));
	}

	protected onHeightChange(value: number | null): void {
		this.height.set(value ?? 0);
		this.aspect.set(matchAspectPreset(this.width(), this.height()));
	}

	// ── Validation ───────────────────────────────────────────────────────────

	protected readonly nameError = computed(() => (this.name().trim() ? null : 'Name is required.'));

	protected readonly slugError = computed(() => {
		const slug = this.slug().trim();
		if (!slug) return 'Slug is required.';
		if (!SLUG_PATTERN.test(slug)) return 'Lowercase letters, digits and single dashes only.';
		if (this.takenSlugs().includes(slug)) return 'A project with this slug already exists in this campaign.';
		return null;
	});

	protected readonly sizeError = computed(() => {
		const w = this.width();
		const h = this.height();
		if (!Number.isInteger(w) || w <= 0 || !Number.isInteger(h) || h <= 0) return 'Width and height must be positive whole numbers.';
		return null;
	});

	protected readonly canConfirm = computed(() =>
		!this.nameError() && !this.slugError() && !this.sizeError() && !this.starting(),
	);

	/** Whether the copy will be a different shape than the source — gates the coordinates warning. */
	protected readonly formatChanged = computed(() =>
		this.width() !== this.sourceWidth() || this.height() !== this.sourceHeight(),
	);

	// ── Run state ────────────────────────────────────────────────────────────

	protected readonly isRunning = computed(() => this.run()?.status === 'running');
	protected readonly isDone = computed(() => this.run()?.status === 'done');
	protected readonly isError = computed(() => this.run()?.status === 'error');
	protected readonly failureCount = computed(() => this.run()?.failures.length ?? 0);
	protected readonly hasFailures = computed(() => this.failureCount() > 0);

	/**
	 * Percentage for the bar. Byte-based, not file-count-based: a project's
	 * files vary from a 3 KB subtitle to a 300 MB video, so counting files
	 * makes the bar jump and stall. Falls back to the file count when the
	 * planned byte total is 0 (nothing to copy, or sizes unknown).
	 */
	protected readonly progressPercent = computed(() => {
		const run = this.run();
		if (!run) return 0;
		if (run.status === 'done') return 100;
		if (run.totalBytes > 0) return Math.min(99, Math.round((run.bytesCopied / run.totalBytes) * 100));
		if (run.totalFiles > 0) return Math.min(99, Math.round((run.copiedFiles / run.totalFiles) * 100));
		return run.phase === 'files' ? 99 : 5;
	});

	protected readonly progressLabel = computed(() => {
		const run = this.run();
		if (!run) return '';
		if (run.status === 'error') return run.error ?? 'Copy failed.';
		if (run.status === 'done') {
			return run.failures.length > 0
				? `Copied ${run.copiedFiles} of ${run.totalFiles} files — ${run.failures.length} failed.`
				: `Copied ${run.totalFiles} files (${formatBytes(run.totalBytes)}).`;
		}
		if (run.phase === 'documents') return 'Copying scenes, images and settings…';
		return `Copying media — ${formatBytes(run.bytesCopied)} of ${formatBytes(run.totalBytes)} (${run.copiedFiles}/${run.totalFiles} files)`;
	});

	// ── Actions ──────────────────────────────────────────────────────────────

	protected onConfirm(): void {
		if (!this.canConfirm()) return;
		const dimensionsMap = ASPECT_PRESETS[this.aspect()]?.dims ?? deriveDimensionsMap(this.width(), this.height());
		this.confirmed.emit({
			name: this.name().trim(),
			slug: this.slug().trim(),
			// Only send a format override when it actually differs — an
			// unchanged format must not rewrite the source's own
			// dimensionsMap shape onto the copy.
			...(this.formatChanged() && {
				dimensions: {
					origCoordsWidth: this.width(),
					origCoordsHeight: this.height(),
					dimensionsMap,
				},
			}),
		});
	}

	protected onOpenCopy(): void {
		const run = this.run();
		if (run) this.openCopy.emit(run);
	}

	protected onClose(): void {
		this.visibleChange.emit(false);
	}

	protected onVisibleChange(value: boolean): void {
		this.visibleChange.emit(value);
	}

	protected formatSize(bytes: number): string {
		return formatBytes(bytes);
	}
}
